<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { getCurrentInstance, onMounted } from 'vue';

import type { Selection } from '@/types/configuration';

import WidgetEditorSearchEngineModal from '@/components/editor/WidgetEditorSearchEngineModal.vue';
import { useEditor } from '@/composables/useEditor';
import { i18n } from '@/shared-instance';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

const props = defineProps<{
  config?: string;
}>();

i18n.install(getCurrentInstance()!.appContext.app);

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);

const instance = getCurrentInstance();
const editorService = useEditor();

function sendConfig(config: string) {
  const hostElement = instance?.vnode.el?.getRootNode()?.host;
  if (hostElement) {
    const event = new CustomEvent('config', {
      detail: [config], // Pass your config string here directly
      bubbles: true, // Allows the event to bubble up the DOM tree
      composed: true, // CRITICAL: Allows the event to escape the Shadow DOM
    });
    hostElement.dispatchEvent(event);
  }
  close();
}

function close() {
  const hostElement = instance?.vnode.el?.getRootNode()?.host;
  if (hostElement) {
    hostElement.remove();
  }
  else {
    console.warn('Could not find the host element to remove.');
  }
}

onMounted(async () => {
  if (!globalConfiguration.value)
    throw new Error('Widget global configuration is missing');
  const apiKey = globalConfiguration.value.apikey ?? null;
  if (apiKey === null || apiKey === undefined) {
    throw new Error('api-key attribute for widget editor is missing');
  }
  else {
    globalConfiguration.value.apikey = apiKey;
  }
  if (props.config != null) {
    const config = JSON.parse(props.config) as Selection;
    if (config.taxonomies != null && config.taxonomies.length) {
      editorService.loadConfig(config);
    }
  }
});
</script>

<template>
  <WidgetEditorSearchEngineModal @config="(v) => sendConfig(v)" @close="close" />
</template>
